import { reportResult, paraDetail, RunDetail, BULLET_CHAR_SET, isSymbolFont } from '../runs/common';
import { getConfig } from '../taskpane/configService';


// ─────────────────────────────────────────────────────────────────────────
// Shared interfaces  (fix S107 — replaces long parameter lists)
// ─────────────────────────────────────────────────────────────────────────

/** Mutable counters threaded through every conversion function. */
interface ConversionCounters {
  parasConverted: number;   // uniform-font paras converted at para level
  runsConverted:  number;   // runs converted inside mixed+symbol paras
  runsSkipped:    number;   // symbol/bullet runs preserved
  parasClubbed:   number;   // mixed+symbol paras clubbed into one comment each
}

/** Everything a converter needs from the Word API context. */
interface ConversionContext {
  context:    Word.RequestContext;
  targetFont: string;
}

/** The accumulated result that is reported at the end. */
interface ConversionResult {
  counters:     ConversionCounters;
  changedParas: RunDetail[];
}

/** A classified partition of ranges inside one paragraph. */
interface RangePartition {
  symbolRuns:  Word.Range[];
  convertRuns: Word.Range[];
}


// ─────────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────────

function isBulletOnlyText(text: string): boolean {
  if (!text) return false;
  for (const ch of text) {
    if (ch !== ' ' && ch !== '\t' && ch !== '\n' && ch !== '\r' && !BULLET_CHAR_SET.has(ch)) {
      return false;
    }
  }
  return true;
}

function containsPuaSymbol(text: string): boolean {
  if (!text) return false;
  for (const ch of text) {
    const cp = ch.codePointAt(0);
    if (cp !== undefined && cp >= 0xE000 && cp <= 0xF8FF) return true;
  }
  return false;
}

function classifyRange(
  range: Word.Range,
  targetFont: string
): 'alreadyTarget' | 'symbol' | 'convert' {
  const runFont = (range.font.name || '').trim();
  const runText = range.text || '';

  if (runFont === targetFont)     return 'alreadyTarget';
  if (isSymbolFont(runFont))      return 'symbol';
  if (isBulletOnlyText(runText))  return 'symbol';
  if (containsPuaSymbol(runText)) return 'symbol';
  return 'convert';
}

function partitionRanges(ranges: Word.Range[], targetFont: string): RangePartition {
  const symbolRuns:  Word.Range[] = [];
  const convertRuns: Word.Range[] = [];

  for (const range of ranges) {
    const kind = classifyRange(range, targetFont);
    if (kind === 'symbol')  symbolRuns.push(range);
    if (kind === 'convert') convertRuns.push(range);
    // 'alreadyTarget' intentionally dropped — no action needed
  }
  return { symbolRuns, convertRuns };
}


// ─────────────────────────────────────────────────────────────────────────
// Run-level conversion (mixed font + symbol runs present)
// ─────────────────────────────────────────────────────────────────────────

async function applyRunLevelConversion(
  para:      Word.Paragraph,
  location:  string,
  paraText:  string,
  partition: RangePartition,
  ctx:       ConversionContext,
  result:    ConversionResult
): Promise<void> {
  const { convertRuns, symbolRuns } = partition;
  const { context, targetFont }     = ctx;

  console.log(
    `${location} Clubbed run-level ` +
    `(${convertRuns.length} runs, ${symbolRuns.length} symbol preserved)`
  );

  context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
  await context.sync();

  for (const range of convertRuns) {
    range.font.name = targetFont;
  }
  await context.sync();

  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  await context.sync();

  para
    .getRange(Word.RangeLocation.whole)
    .insertComment(
      `Font converted to ${targetFont} ` +
      `(${convertRuns.length} run(s) changed, ${symbolRuns.length} symbol run(s) preserved)`
    );
  await context.sync();

  result.counters.runsConverted += convertRuns.length;
  result.counters.runsSkipped   += symbolRuns.length;
  result.counters.parasClubbed++;

  result.changedParas.push(paraDetail(
    para.uniqueLocalId,
    paraText,
    `mixed+symbol \u2192 ${targetFont} (${convertRuns.length} runs)`
  ));
}


// ─────────────────────────────────────────────────────────────────────────
// Fallback handler (when run-level conversion throws)
// ─────────────────────────────────────────────────────────────────────────

function handleConversionFallback(
  para:     Word.Paragraph,
  location: string,
  paraText: string,
  error:    Error,
  ctx:      ConversionContext,
  result:   ConversionResult
): void {
  const { targetFont }   = ctx;
  const paraFontName     = (para.font.name || '').trim();

  console.log(`${location} Run-level failed (${error.message}), falling back to para-level`);

  if (!isSymbolFont(paraFontName) && !containsPuaSymbol(paraText)) {
    para.font.name = targetFont;
    result.counters.parasConverted++;
    result.changedParas.push(
      paraDetail(para.uniqueLocalId, paraText, `fallback \u2192 ${targetFont}`)
    );
  } else {
    console.log(
      `${location} Skipped fallback: contains symbol font or PUA chars (preserving glyphs)`
    );
    result.counters.runsSkipped++;
  }
}


// ─────────────────────────────────────────────────────────────────────────
// Mixed-font paragraph processor (Case 4)
// ─────────────────────────────────────────────────────────────────────────

async function convertMixedFontParagraph(
  para:     Word.Paragraph,
  location: string,
  paraText: string,
  ctx:      ConversionContext,
  result:   ConversionResult
): Promise<void> {
  const { context, targetFont } = ctx;

  try {
    const ranges = para.getTextRanges(
      [' ', '\t', ',', '.', ';', ':', '(', ')', '-'], true
    );
    ranges.load('items');
    await context.sync();

    if (ranges.items.length === 0) return;

    for (const range of ranges.items) {
      range.font.load('name');
      range.load('text');
    }
    await context.sync();

    const partition = partitionRanges(ranges.items, targetFont);
    const { symbolRuns, convertRuns } = partition;

    if (convertRuns.length === 0) {
      result.counters.runsSkipped += symbolRuns.length;
      return;
    }

    // No symbol runs → promote to paragraph-level change (cleaner tracked change)
    if (symbolRuns.length === 0) {
      para.font.name = targetFont;
      result.counters.parasConverted++;
      result.changedParas.push(
        paraDetail(para.uniqueLocalId, paraText, `mixed fonts \u2192 ${targetFont}`)
      );
      console.log(`${location} Para-level (mixed, no symbols): "${paraText.substring(0, 40)}"`);
      return;
    }

    await applyRunLevelConversion(para, location, paraText, partition, ctx, result);

  } catch (e) {
    handleConversionFallback(para, location, paraText, e as Error, ctx, result);
  }
}


// ─────────────────────────────────────────────────────────────────────────
// Core paragraph converter — Cognitive Complexity ≤ 15, params ≤ 7
// ─────────────────────────────────────────────────────────────────────────

async function convertParagraphRuns(
  para:     Word.Paragraph,
  location: string,
  ctx:      ConversionContext,
  result:   ConversionResult
): Promise<void> {
  const { context, targetFont } = ctx;

  para.load('text, uniqueLocalId');
  para.font.load('name');
  await context.sync();

  const paraFontName = (para.font.name || '').trim();
  const paraText     = (para.text     || '').trim();

  // CASE 1: Already target font
  if (paraFontName.startsWith(targetFont)) return;

  // CASE 2: Uniform non-symbol font → single paragraph-level change
  if (paraFontName && !isSymbolFont(paraFontName) && !containsPuaSymbol(paraText)) {
    para.font.name = targetFont;
    result.counters.parasConverted++;
    result.changedParas.push(
      paraDetail(para.uniqueLocalId, paraText, `${paraFontName} \u2192 ${targetFont}`)
    );
    return;
  }

  // CASE 3: Uniform symbol font → skip
  if (paraFontName && isSymbolFont(paraFontName)) {
    result.counters.runsSkipped++;
    console.log(`${location} SKIP symbol para font "${paraFontName}": "${paraText.substring(0, 30)}"`);
    return;
  }

  // CASE 4: Mixed fonts (paraFont is null/empty)
  await convertMixedFontParagraph(para, location, paraText, ctx, result);
}


// ─────────────────────────────────────────────────────────────────────────
// Body paragraph processor
// ─────────────────────────────────────────────────────────────────────────

async function processBodyParagraphs(
  ctx:    ConversionContext,
  result: ConversionResult
): Promise<void> {
  const paragraphs = ctx.context.document.body.paragraphs;
  paragraphs.load('items');
  await ctx.context.sync();

  console.log(`Body paragraphs: ${paragraphs.items.length}`);

  for (let i = 0; i < paragraphs.items.length; i++) {
    await convertParagraphRuns(paragraphs.items[i], `[Body ${i + 1}]`, ctx, result);
  }
}


// ─────────────────────────────────────────────────────────────────────────
// Table cell paragraph processor
// ─────────────────────────────────────────────────────────────────────────

async function processTableParagraphs(
  ctx:    ConversionContext,
  result: ConversionResult
): Promise<void> {
  const { context } = ctx;
  const tables = context.document.body.tables;
  tables.load('items');
  await context.sync();

  for (const table of tables.items) { table.rows.load('items'); }
  await context.sync();
  for (const table of tables.items) {
    for (const row of table.rows.items) { row.cells.load('items'); }
  }
  await context.sync();

  for (let t = 0; t < tables.items.length; t++) {
    for (let r = 0; r < tables.items[t].rows.items.length; r++) {
      for (let c = 0; c < tables.items[t].rows.items[r].cells.items.length; c++) {
        const cell      = tables.items[t].rows.items[r].cells.items[c];
        const cellParas = cell.body.paragraphs;
        cellParas.load('items');
        await context.sync();

        for (let p = 0; p < cellParas.items.length; p++) {
          await convertParagraphRuns(
            cellParas.items[p],
            `[T${t + 1} R${r + 1} C${c + 1} P${p + 1}]`,
            ctx,
            result
          );
        }
      }
    }
  }
}


// ─────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────

export async function run1() {
  const cfg        = getConfig();
  const targetFont = cfg.run1_font.targetFont;

  await Word.run(async (context) => {

    // Start with tracking ON — Cases 2 and 4a produce clean tracked changes.
    // Case 4b (run-level) temporarily turns it off internally to club run edits.
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

    const ctx: ConversionContext = { context, targetFont };

    const result: ConversionResult = {
      counters: {
        parasConverted: 0,
        runsConverted:  0,
        runsSkipped:    0,
        parasClubbed:   0,
      },
      changedParas: [],
    };

    console.log('='.repeat(70));
    console.log(`=== FONT CONVERSION TO ${targetFont.toUpperCase()} (${cfg.clientName}) ===`);
    console.log('='.repeat(70));

    await processBodyParagraphs(ctx, result);
    await processTableParagraphs(ctx, result);
    await context.sync();

    // Ensure tracking ends OFF
    context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
    await context.sync();

    const { counters, changedParas } = result;

    console.log('\n' + '='.repeat(70));
    console.log(`Paragraphs converted (para-level):     ${counters.parasConverted}`);
    console.log(`Paragraphs clubbed   (run+comment):    ${counters.parasClubbed}`);
    console.log(`Runs converted (inside clubbed paras): ${counters.runsConverted}`);
    console.log(`Runs/paras skipped (symbol/bullet):    ${counters.runsSkipped}`);
    console.log('\u2713 Font conversion complete!');
    console.log('='.repeat(70));

    const total = counters.parasConverted + counters.runsConverted + counters.parasClubbed;
    if (total > 0) {
      reportResult(`Font Conversion to ${targetFont} Complete`, [...changedParas]);
    } else {
      reportResult('Font Conversion Complete \u2014 no changes needed', [
        { label: `All text already in ${targetFont}`, note: 'No changes made' },
      ]);
    }
  });
}